/**
 * Vuex Mutations for Notification Management
 * Pure functions that modify the notification state in predictable ways
 * Each mutation handles a specific state update scenario
 */

/**
 * Replace the entire notifications array with new data
 * Used for initial load or filter changes
 * @param {Object} state - Vuex state object
 * @param {Array} notifications - New array of notification objects
 */
export function SET_NOTIFICATIONS(state, notifications) {
  state.notifications = notifications;
}

/**
 * Append new notifications to existing array
 * Used for pagination and "load more" functionality
 * @param {Object} state - Vuex state object
 * @param {Array} notifications - Additional notifications to append
 */
export function APPEND_NOTIFICATIONS(state, notifications) {
  state.notifications = [...state.notifications, ...notifications];
}

/**
 * Update the count of unread notifications
 * Used for badge counters and UI indicators
 * @param {Object} state - Vuex state object
 * @param {number} count - New unread count value
 */
export function SET_UNREAD_COUNT(state, count) {
  state.unreadCount = count;
}

/**
 * Set main loading state for initial data fetch
 * Controls primary loading spinner
 * @param {Object} state - Vuex state object
 * @param {boolean} loading - Loading state flag
 */
export function SET_LOADING(state, loading) {
  state.loading = loading;
}

/**
 * Set loading state for pagination requests
 * Controls "load more" spinner
 * @param {Object} state - Vuex state object
 * @param {boolean} loadingMore - Loading more state flag
 */
export function SET_LOADING_MORE(state, loadingMore) {
  state.loadingMore = loadingMore;
}

/**
 * Set loading state for "mark all as read" operation
 * Prevents duplicate operations and provides user feedback
 * @param {Object} state - Vuex state object
 * @param {boolean} marking - Marking all as read state flag
 */
export function SET_MARKING_ALL_AS_READ(state, marking) {
  state.markingAllAsRead = marking;
}

/**
 * Set whether more notifications are available to load
 * Controls "load more" button visibility and infinite scroll
 * @param {Object} state - Vuex state object
 * @param {boolean} hasMore - Availability of more notifications
 */
export function SET_HAS_MORE(state, hasMore) {
  state.hasMore = hasMore;
}

/**
 * Update current pagination page
 * Tracks position in notification list for pagination
 * @param {Object} state - Vuex state object
 * @param {number} page - Current page number
 */
export function SET_CURRENT_PAGE(state, page) {
  state.currentPage = page;
}

/**
 * Update current notification filter
 * Controls which notifications are displayed
 * @param {Object} state - Vuex state object
 * @param {string} filter - Filter type: 'all' or 'unread'
 */
export function SET_FILTER(state, filter) {
  state.filter = filter;
}

/**
 * Mark a specific notification as read
 * Updates individual notification read status and maintains UI state
 * @param {Object} state - Vuex state object
 * @param {string} notificationId - ID of notification to mark as read
 */
export function MARK_AS_READ(state, notificationId) {
  const notification = state.notifications.find((n) => n.id === notificationId);
  if (notification && !notification.read) {
    notification.read = true;
    state.unreadCount = Math.max(0, state.unreadCount - 1);
  }
}

/**
 * Mark all notifications as read
 * Updates all notifications in current view and resets unread count
 * @param {Object} state - Vuex state object
 */
export function MARK_ALL_AS_READ(state) {
  let unreadCountReduction = 0;

  state.notifications.forEach((notification) => {
    if (!notification.read) {
      notification.read = true;
      unreadCountReduction++;
    }
  });

  state.unreadCount = Math.max(0, state.unreadCount - unreadCountReduction);
}

/**
 * Add a new notification to the beginning of the list
 * Used for real-time notifications and push updates
 * @param {Object} state - Vuex state object
 * @param {Object} notification - New notification object to add
 */
export function ADD_NOTIFICATION(state, notification) {
  state.notifications.unshift(notification);
  if (!notification.read) {
    state.unreadCount += 1;
  }
}

/**
 * Remove a specific notification from the list
 * Handles notification deletion and unread count adjustment
 * @param {Object} state - Vuex state object
 * @param {string} notificationId - ID of notification to remove
 */
export function REMOVE_NOTIFICATION(state, notificationId) {
  const index = state.notifications.findIndex((n) => n.id === notificationId);
  if (index !== -1) {
    const notification = state.notifications[index];
    if (!notification.read) {
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    }
    state.notifications.splice(index, 1);
  }
}

/**
 * Clear all notifications and reset pagination state
 * Used for logout, filter reset, or error recovery
 * @param {Object} state - Vuex state object
 */
export function CLEAR_NOTIFICATIONS(state) {
  state.notifications = [];
  state.unreadCount = 0;
  state.currentPage = 1;
  state.hasMore = false;
}
